import { MAX_TICKET_TITLE_LENGTH } from "@jugl-web/domain-resources/tickets/consts";
import { UpdateTicketPayload } from "@jugl-web/rest-api";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import {
  InteractiveContainer,
  InteractiveContainerProps,
  TableCell,
} from "@jugl-web/ui-kit";
import { ClickAwayListener, cx } from "@jugl-web/utils";
import { HeaderIconButton } from "@web-src/modules/client-support/components/HeaderIconButton";
import { useClientSupportPageContext } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useTicketsViewContext } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/TicketsViewProvider";
import { FC, FocusEvent, KeyboardEvent, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { ReactComponent as ExpandIcon } from "./assets/expand.svg";

interface TicketNameCellContentProps {
  ticket: TicketListItemModel;
  onUpdate: (data: UpdateTicketPayload) => void;
}

export const TicketNameCellContent: FC<TicketNameCellContentProps> = ({
  ticket,
  onUpdate,
}) => {
  const [textareaValue, setTextareaValue] = useState(ticket.title);

  const { openTicketDetailsSidebar } = useClientSupportPageContext();

  const { searchQuery } = useTicketsViewContext();

  const [isEditing, setIsEditing] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleTitleClick: InteractiveContainerProps["onClick"] = (event) => {
    event.stopPropagation();
    setIsEditing(true);
    setTextareaValue(ticket.title);
  };

  const handleFinishTitleEditing = () => {
    setIsEditing(false);

    if (!textareaRef.current) {
      return;
    }

    const newTitle = textareaRef.current.value.trim();

    if (newTitle.length === 0 || newTitle === ticket.title) {
      return;
    }

    onUpdate({ title: newTitle });
  };

  const handleTextareaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit the form when Enter is pressed (without Shift)
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      handleFinishTitleEditing();
      return;
    }

    // Cancel the editing when Escape is pressed
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setIsEditing(false);
    }
  };

  const handleTextareaFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
    // Put the cursor at the end of the textarea
    event.target.setSelectionRange(ticket.title.length, ticket.title.length);

    // Scroll to the bottom of the textarea
    event.target.scrollTo({
      top: event.target.scrollHeight,
      behavior: "smooth",
    });
  };

  const hasReachedMaxLength = textareaValue.length >= MAX_TICKET_TITLE_LENGTH;

  if (isEditing) {
    return (
      <TableCell
        classNames={{
          content: "h-full",
          cell: cx(
            "p-0 outline-1 bg-white",
            hasReachedMaxLength ? "min-h-[146px]" : "min-h-[120px]"
          ),
        }}
      >
        <ClickAwayListener
          className="flex h-full w-full flex-col"
          onClickAway={handleFinishTitleEditing}
        >
          <textarea
            ref={textareaRef}
            value={textareaValue}
            onChange={(e) => setTextareaValue(e.target.value)}
            onFocus={handleTextareaFocus}
            onKeyDown={handleTextareaKeyDown}
            autoFocus
            className="jugl__custom-scrollbar w-full flex-1 resize-none p-4 font-secondary text-sm leading-[140%] font-medium tracking-[0.14px] text-dark outline-none"
            maxLength={MAX_TICKET_TITLE_LENGTH}
          />
          {hasReachedMaxLength && (
            <div className="mt-2 shrink-0 px-4 pb-2">
              <span className="text-xs leading-[150%] font-normal text-tertiary-700">
                {textareaValue.length}/{MAX_TICKET_TITLE_LENGTH}
              </span>
            </div>
          )}
        </ClickAwayListener>
      </TableCell>
    );
  }

  return (
    <TableCell
      classNames={{ content: "flex min-w-0 flex-col gap-1 overflow-visible" }}
    >
      <div className="flex w-full min-w-0 items-center gap-1">
        <InteractiveContainer
          onClick={handleTitleClick}
          className={cx(
            "flex h-8 min-w-0 flex-1 items-center overflow-hidden transition-all",
            "rounded-md border border-solid border-transparent bg-transparent hover:border-grey-300 hover:bg-white hover:pl-1"
          )}
          title={ticket.title}
        >
          <span className="block min-w-0 truncate text-sm font-medium tracking-[0.14px] text-dark">
            {searchQuery ? (
              <Highlighter
                autoEscape
                highlightClassName="text-primary font-medium"
                highlightTag="span"
                textToHighlight={ticket.title}
                searchWords={[searchQuery]}
              />
            ) : (
              ticket.title
            )}
          </span>
        </InteractiveContainer>
        <HeaderIconButton
          icon={<ExpandIcon />}
          className="hidden shrink-0 group-hover/cell:block"
          title="Open Ticket"
          onClick={() => openTicketDetailsSidebar(ticket.id)}
        />
      </div>
      <span className="truncate text-xs leading-[140%] font-normal text-dark-600">
        {searchQuery ? (
          <Highlighter
            autoEscape
            highlightClassName="text-primary font-medium"
            highlightTag="span"
            textToHighlight={`#${ticket.unique_id}`}
            searchWords={[searchQuery]}
          />
        ) : (
          `#${ticket.unique_id}`
        )}
      </span>
    </TableCell>
  );
};
